/**
 * Sidebar navigation item and section header components.
 */

import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  Box,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { COLORS } from './layoutConstants';

/* ── SidebarNavItem ────────────────────────────────────────────────── */

interface SidebarNavItemProps {
  to: string;
  label: string;
  icon: React.ReactNode;
  indent?: boolean;
  disabled?: boolean;
  sidebarExpanded: boolean;
  onNavigate?: () => void;
}

export function SidebarNavItem({
  to,
  label,
  icon,
  indent = false,
  disabled = false,
  sidebarExpanded,
  onNavigate,
}: SidebarNavItemProps) {
  if (disabled) {
    return (
      <ListItem disablePadding sx={{ display: 'block' }}>
        <ListItemButton
          disabled
          sx={{
            minHeight: 44,
            px: sidebarExpanded ? (indent ? 3.5 : 2) : 2,
            justifyContent: sidebarExpanded ? 'initial' : 'center',
            borderRadius: '6px',
            mx: sidebarExpanded ? 1 : 0.5,
            my: 0.25,
            opacity: 0.4,
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: sidebarExpanded ? 36 : 'unset',
              justifyContent: 'center',
              color: COLORS.sectionHeader,
              '& .MuiSvgIcon-root': { fontSize: 20 },
            }}
          >
            {icon}
          </ListItemIcon>
          {sidebarExpanded && (
            <ListItemText
              primary={label}
              primaryTypographyProps={{
                fontSize: 14,
                fontWeight: 500,
                color: COLORS.sectionHeader,
              }}
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  }

  return (
    <NavLink
      to={to}
      style={{ textDecoration: 'none', display: 'block' }}
      onClick={onNavigate}
    >
      {({ isActive }) => (
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            sx={{
              minHeight: 44,
              px: sidebarExpanded ? (indent ? 3.5 : 2) : 2,
              justifyContent: sidebarExpanded ? 'initial' : 'center',
              borderRadius: '6px',
              mx: sidebarExpanded ? 1 : 0.5,
              my: 0.25,
              position: 'relative',
              backgroundColor: isActive ? COLORS.sidebarHoverBg : 'transparent',
              // Active left accent bar
              '&::before': isActive
                ? {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 3,
                    height: '60%',
                    borderRadius: '0 3px 3px 0',
                    backgroundColor: COLORS.accentPurple,
                  }
                : {},
              '&:hover': {
                backgroundColor: COLORS.sidebarHoverBg,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 3,
                  height: '60%',
                  borderRadius: '0 3px 3px 0',
                  backgroundColor: COLORS.accentPurple,
                },
                '& .nav-label': {
                  color: COLORS.navTextHover,
                },
                '& .nav-icon': {
                  color: COLORS.navTextHover,
                },
              },
            }}
          >
            <ListItemIcon
              className="nav-icon"
              sx={{
                minWidth: sidebarExpanded ? 36 : 'unset',
                justifyContent: 'center',
                color: isActive ? COLORS.navTextActive : COLORS.navText,
                transition: 'color 0.15s ease',
                '& .MuiSvgIcon-root': { fontSize: 20 },
              }}
            >
              {icon}
            </ListItemIcon>
            {sidebarExpanded && (
              <ListItemText
                className="nav-label"
                primary={label}
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? COLORS.navTextActive : COLORS.navText,
                  sx: { transition: 'color 0.15s ease' },
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
      )}
    </NavLink>
  );
}

/* ── SectionHeader ─────────────────────────────────────────────────── */

interface SectionHeaderProps {
  label: string;
  collapsible?: boolean;
  open?: boolean;
  onToggle?: () => void;
  sidebarExpanded: boolean;
}

export function SectionHeader({
  label,
  collapsible = false,
  open,
  onToggle,
  sidebarExpanded,
}: SectionHeaderProps) {
  if (!sidebarExpanded) return null;

  return (
    <Box
      onClick={collapsible ? onToggle : undefined}
      sx={{
        px: 2,
        pt: 2,
        pb: 0.5,
        mx: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: collapsible ? 'pointer' : 'default',
        userSelect: 'none',
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: COLORS.sectionHeader,
        }}
      >
        {label}
      </Typography>
      {collapsible && (
        <Box sx={{ color: COLORS.sectionHeader, display: 'flex', alignItems: 'center' }}>
          {open ? (
            <ExpandLess sx={{ fontSize: 16 }} />
          ) : (
            <ExpandMore sx={{ fontSize: 16 }} />
          )}
        </Box>
      )}
    </Box>
  );
}
